import {
    trackPageview,
    trackEcommerce,
    trackingConstants,
    eventNameConstants,
    getSessionPageCount,
    getCustomDimensions,
    trackUserEventItemPageView,
    getUserEventItem,
} from 'dibs-tracking';
import serverVars from 'server-vars';
import { useEffect, useRef } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { useBuyerUrgency } from 'dibs-buyer-urgency/exports/useBuyerUrgency';
import { getEstimationTrackingValue } from '../PdpSharedPricingGuidance/pricingGuidanceTracking';
import {
    SELLER_TIER_DIAMOND,
    SELLER_TIER_PLATINUM,
    SELLER_TIER_GOLD,
    SELLER_TIER_VETTED,
} from '../messages/pdpDistinctionsMessages';
import { trackSellerBrandingRemovalAbTestVariant } from 'dibs-buyer-layout/exports/sellerBrandingRemovalAbTestHelpers';

// types
import { type usePdpSharedTracking_item$key as Item } from './__generated__/usePdpSharedTracking_item.graphql';
import { type usePdpSharedTracking_viewer$key as Viewer } from './__generated__/usePdpSharedTracking_viewer.graphql';
import { type usePdpSharedTracking_user$key as User } from './__generated__/usePdpSharedTracking_user.graphql';

const ADDITIONAL_TRACKING_DEFAULTS = {
    isCondensed: 'N',
    personalization: 'false',
};

const CUSTOM_DIMENSIONS_TO_TRACK = [
    'onSale',
    'newListing',
    'dealerName',
    'dealerID',
    'dealerLocation',
    'itemPostingLocation',
    'purchaseStatus',
    'askingPrice',
    'priceType',
];

const viewerFragment = graphql`
    fragment usePdpSharedTracking_viewer on Viewer {
        ...useBuyerUrgency_viewer @arguments(itemId: $itemId, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment usePdpSharedTracking_user on User {
        canShowSellerName
    }
`;

const itemFragment = graphql`
    fragment usePdpSharedTracking_item on Item {
        ... @include(if: $afterDisplay) {
            browseUrl
            categoryCode
            contemporaryTrackingString
            pdpTrackingVpvParams(
                userId: $userId
                userCountryCode: $userCountryCode
                prequoteBlockListTestArm: $prequoteBlockListTestArm
            )
            viewInRoomPhotos {
                __typename
            }
            seller {
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                sellerTiers {
                    isGoldTier
                    isPlatinumTier
                }
            }
            ...pricingGuidanceTracking_item
        }
    }
`;

export const usePdpSharedTracking = ({
    item: itemRef,
    viewer: viewerRef,
    user: userRef,
    additionalTrackingParams = {},
}: {
    item: Item;
    viewer: Viewer;
    user?: User | null;
    additionalTrackingParams?: Record<string, string>;
}): void => {
    const environment = useRelayEnvironment();
    const hasFiredTracking = useRef<boolean>(false);
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const { showBuyerUrgencyBanner: buyerUrgency } = useBuyerUrgency({ viewer });

    const { pdpTrackingVpvParams, seller } = item;
    const { isRewardsTier2, sellerTiers } = seller || {};
    const { isGoldTier, isPlatinumTier } = sellerTiers || {};
    const { url, ...additional } = pdpTrackingVpvParams || {};
    const { canShowSellerName } = user || {};

    useEffect(() => {
        if (url && !hasFiredTracking.current) {
            const { browseUrl, categoryCode, viewInRoomPhotos, contemporaryTrackingString } = item;
            const pageCountViewed = getSessionPageCount();
            const hasViewInroom = !!viewInRoomPhotos?.length;
            const soldPrice = getEstimationTrackingValue({ item });

            let dealerStanding = SELLER_TIER_VETTED;
            if (isGoldTier) {
                dealerStanding = SELLER_TIER_GOLD;
            } else if (isPlatinumTier) {
                dealerStanding = SELLER_TIER_PLATINUM;
            } else if (isRewardsTier2) {
                dealerStanding = SELLER_TIER_DIAMOND;
            }

            if (canShowSellerName) {
                trackSellerBrandingRemovalAbTestVariant();
            }

            trackPageview({
                url,
                additional: {
                    soldPrice,
                    buyerUrgency,
                    hasViewInroom,
                    pageCountViewed,
                    dealerStanding,
                    opt_pageURL: url,
                    ...additional,
                    ...{
                        ...ADDITIONAL_TRACKING_DEFAULTS,
                        ...additionalTrackingParams,
                    },
                },
            });
            const userEventItem = getUserEventItem({
                id: pdpTrackingVpvParams?.itemId,
                amount: pdpTrackingVpvParams?.price,
            });
            if (userEventItem) {
                trackUserEventItemPageView(environment, [userEventItem]);
            }
            trackEcommerce({
                type: trackingConstants.ECOM_PRODUCT_DETAIL,
                eventName: eventNameConstants.EVENT_VIEW_ITEM,
                products: [
                    {
                        category: browseUrl,
                        id: additional.itemId,
                        categoryCode: categoryCode,
                        brand: additional.dealerId,
                        price: additional.askingPrice,
                        dimension12: additional.dealerId,
                        dimension83: contemporaryTrackingString || additional.contemporary,
                        name: serverVars.get('locale') === 'en-US' ? additional.itemName : 'n/a',
                        ...getCustomDimensions(additional, CUSTOM_DIMENSIONS_TO_TRACK),
                    },
                ],
            });
            hasFiredTracking.current = true;
        }
    }, [
        item,
        additional,
        url,
        buyerUrgency,
        additionalTrackingParams,
        pdpTrackingVpvParams,
        environment,
        isGoldTier,
        isPlatinumTier,
        isRewardsTier2,
        canShowSellerName,
    ]);
};
